import Custom500 from './500';
import type { InferGetServerSidePropsType, GetServerSideProps } from 'next';
import { PageModel } from '@/types';
import {
  CMSModule,
  PageContainer,
  Metadata,
  Header,
  SystemFeaturesContext,
  MetadataContext,
  UserContext,
  MediaGalleryContextElement,
  HeaderApp,
  FooterApp,
  RecommendationsContextElement,
} from '@/components';
import { DownloadHubProvider } from '@/components';
import { OptimizedPreviewProvider } from '@/components/scrapbook/previews/OptimizedPreview';
import { getPageData, getTranslationMessages } from '@/api';
import classNames from 'classnames';
import Footer from '@/modules/Footer';
import { authOptions } from './api/auth/[...nextauth]';
import { getServerSession } from 'next-auth/next';
import { useEffect } from 'react';
import { setVisitedPageId } from '@/utils/visitedPages';
import { getHost, getProtocol } from '@/utils';
import ReportPageWrapper from '@/components/report/ReportPageWrapper';
import { fixPageModules } from '@/utils/module-utils';
import { NovaProvider } from '@/components/Nova';
import { isNovaLandingModule } from '@/components/Nova/novaTaxonomy';

export default function Page({
  errorCode,
  errorMessage,
  data,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  // Track visited pages
  useEffect(() => {
    if (data?.metaData?.pageId) {
      setVisitedPageId(data.metaData.pageId);
    }
  }, [data?.metaData?.pageId]);

  if (errorCode) {
    return <Custom500 errorCode={errorCode} errorMessage={errorMessage || 'An unexpected error occurred.'} />;
  }

  if (!data) return null;

  const { moduleContent, metaData, header, footer, features, user, transparentHeader, fullPageSlider } = data;

  const isReportPage = metaData?.pageType === 'reportPage' || metaData?.pageType === 'legacyReportPage';
  const isNovaTool = moduleContent?.some((module) => isNovaLandingModule(module.moduleName));

  const pageContent = (
    <>
      <Metadata metadata={metaData} />
      {metaData.isPublic || metaData.isStub ? (
        <Header
          key={metaData.pageId}
          data={header}
          transparentHeader={transparentHeader}
          animate={transparentHeader}
          fullPageSlider={fullPageSlider}
        />
      ) : (
        <>
          <HeaderApp data={header} />
        </>
      )}
      <PageContainer
        className={classNames(
          transparentHeader ? 'mt-[var(--header-height)] md:mt-0' : 'mt-[var(--header-height)]',
          !(metaData.isPublic || metaData.isStub) &&
            (isNovaTool
              ? 'flex h-[calc(100dvh-var(--header-height))] flex-col overflow-hidden'
              : 'flex min-h-[calc(100vh-var(--header-height))] flex-col'),
        )}
        fullPageSlider={fullPageSlider}
      >
        {moduleContent?.map((module) => (
          <CMSModule key={module.id} module={module} />
        ))}
        {!isNovaTool && (metaData.isPublic || metaData.isStub ? <Footer {...footer} /> : <FooterApp {...footer} />)}
      </PageContainer>
    </>
  );

  return (
    <SystemFeaturesContext value={features || {}}>
      <MetadataContext value={metaData}>
        <UserContext value={user || {}}>
          <RecommendationsContextElement userId={user?.userId}>
            <DownloadHubProvider>
              <OptimizedPreviewProvider>
                <MediaGalleryContextElement>
                  <ReportPageWrapper
                    pageId={metaData?.pageId?.toString() || ''}
                    pageTitle={metaData?.pageTitle || ''}
                    userId={user?.userId}
                    isReportPage={isReportPage}
                    isAuthenticated={user?.isAuthenticated}
                    isIpBasedIdentity={user?.isIpBasedIdentity}
                    showSaveToScrapbook={features?.saveToScrapbook && user?.isAuthenticated}
                  >
                    <NovaProvider>{pageContent}</NovaProvider>
                  </ReportPageWrapper>
                </MediaGalleryContextElement>
              </OptimizedPreviewProvider>
            </DownloadHubProvider>
          </RecommendationsContextElement>
        </UserContext>
      </MetadataContext>
    </SystemFeaturesContext>
  );
}

export const getServerSideProps = (async (context) => {
  const { resolvedUrl, locale, defaultLocale, res, req } = context;

  // workaround for missing assets spamming the API
  if (resolvedUrl.includes('/_next/') || resolvedUrl.includes('/_nuxt/') || resolvedUrl.includes('/api/api/')) {
    res.statusCode = 404;
    res.end();
    return {
      props: {},
    };
  }

  const session = await getServerSession(req, res, authOptions);
  const accessToken = (session as any)?.access_token; // from session callback

  let errorCode: number = 200;
  let errorMessage: string | null = null;
  try {
    const dataRes = await getPageData(resolvedUrl, locale, defaultLocale, req, accessToken);
    errorCode = dataRes?.status;
    let redirectToLogin = false;
    const contentType = dataRes?.headers.get('content-type');
    if (errorCode === 500) {
      errorMessage =
        process.env.NODE_ENV === 'development'
          ? 'BE Internal Server Error'
          : "An error has occurred and we're working to fix the problem.";
      res.statusCode = 500;
      console.error('[MP] BE Internal Server Error');
    } else if (!contentType || contentType.indexOf('application/json') === -1) {
      if (errorCode === 404) {
        errorMessage = 'The page you were looking for doesn’t exist.';
        res.statusCode = 404;
      } else if (errorCode >= 300 && errorCode < 400) {
        console.warn('Redirecting to:', dataRes.headers.get('Location'));
        return {
          redirect: {
            destination: dataRes.headers.get('Location') || '/',
            permanent: false,
          },
          props: { errorCode: null, errorMessage: null },
        };
      } else {
        errorCode = 500;
        errorMessage =
          process.env.NODE_ENV === 'development'
            ? 'BE response not JSON Object'
            : "An error has occurred and we're working to fix the problem.";
        res.statusCode = 500;
        const responseText = await dataRes.text();
        console.error('[MP] BE response not JSON Object. contentType:', contentType, 'responseText:', responseText);
      }
    } else if (errorCode === 401) {
      redirectToLogin = true;
    } else if (errorCode === 200 || errorCode === 404) {
      const data: PageModel = await dataRes.json();

      if (data.requiresLogin) {
        redirectToLogin = true;
      }

      if (redirectToLogin) {
        return {
          redirect: {
            destination: `/open-login?redirect=${encodeURIComponent(resolvedUrl)}`,
            permanent: false,
          },
        };
      }

      if (['sp404PageNotFound', 'pageNotFound'].includes(data?.metaData?.pageType) && res) {
        res.statusCode = 404;
      }

      res.setHeader('Cache-Control', data.metaData?.isPublic ? `public, max-age=7200` : 'no-cache');

      let transparentHeader = false;

      const { moduleContent: _moduleContent, fullPageSlider } = fixPageModules(data.moduleContent);

      const absoluteUrl = new URL(resolvedUrl, `https://${req.headers.host}`);
      const path = absoluteUrl.pathname;

      if (
        (['/'].includes(path) ||
          (_moduleContent[0]?.moduleName === 'HeroModule' && res.statusCode !== 404) ||
          (_moduleContent[0]?.moduleName === 'ReportHero' &&
            ['Classic Letterbox', 'Full Width Banner'].includes((_moduleContent[0]?.props as any).heroType)) ||
          (_moduleContent[1]?.moduleName === 'ReportHero' &&
            ['Classic Letterbox', 'Full Width Banner'].includes((_moduleContent[1]?.props as any).heroType))) &&
        data.metaData?.isPublic
      ) {
        transparentHeader = true;
      }

      const host = getHost(req);
      const protocol = getProtocol(req);

      const isGateModule = (_moduleContent.find((module: any) => module.moduleName === 'ScriptModule')?.props as any)
        ?.isGate;

      return {
        props: {
          errorCode: null,
          errorMessage,
          data: {
            ...data,
            metaData: {
              ...data.metaData,
              isPublicReport: data.metaData.siteRootId === 240466,
              isPublicReportIsGated: data.metaData.siteRootId === 240466 && !!isGateModule,
            },
            features: { realHost: `${protocol}//${host}`, ...data.features },
            moduleContent: _moduleContent,
            transparentHeader,
            fullPageSlider,
          },
          messages: await getTranslationMessages(locale || defaultLocale || 'en-GB'),
        },
      };
    }
  } catch (error: any) {
    errorCode = 500;
    console.error('[MP]', error);
    errorMessage = error.message || 'Internal Server Error';
    res.statusCode = 500;
  }

  return {
    props: { errorCode, errorMessage },
  };
}) satisfies GetServerSideProps<{ data?: PageModel }>;
